// Globals
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { IConversationSuggestion, TApproveSuggestedFaqTarget } from '@/models/suggestedFaq.model';

// Modules
import { getSuggestedFaqStatusLabel, isSuggestedFaqActionable, StatusPill } from '@/modules/SuggestedFaq';
import {
  ApproveFaqModal,
  getSuggestedFaqFormValues,
  RejectFaqModal,
  StyledApproveButton,
  SuggestedFaqForm,
  TSuggestedFaqFormValues,
} from '@/modules/SuggestedFaqCommon';
import { useChangeSuggestedFaqStatus } from '@/modules/SuggestedFaqDetails';

// Components
import { useForm } from '@/components';
import { Button } from '@/ui';
import {
  StyledItemActions,
  StyledItemBox,
  StyledItemHeader,
  StyledItemNumber,
  StyledItemTitle,
} from './ConversationSuggestedFaqItemStyles';

// Helpers
import { useBoolean } from '@/hooks';

type TConversationSuggestedFaqItemProps = {
  suggestion: IConversationSuggestion;
  index: number;
};

const ConversationSuggestedFaqItem: FC<TConversationSuggestedFaqItemProps> = ({ suggestion, index }) => {
  const { t } = useTranslation();
  const [formInstance] = useForm();
  const { handleApproveSuggestedFaq, handleRejectSuggestedFaq, isLoading } = useChangeSuggestedFaqStatus(
    suggestion.reviewId,
  );

  const { value: isApproveModalOpen, setTrue: openApproveModal, setFalse: closeApproveModal } = useBoolean();
  const { value: isRejectModalOpen, setTrue: openRejectModal, setFalse: closeRejectModal } = useBoolean();

  const isActionable = isSuggestedFaqActionable(suggestion.status);
  const initialValues = useMemo(() => getSuggestedFaqFormValues(suggestion), [suggestion]);

  const handleOpenApproveModal = async () => {
    try {
      await formInstance.validateFields();
      openApproveModal();
    } catch {}
  };

  const handleApprove = async (target: TApproveSuggestedFaqTarget) => {
    try {
      const values = (await formInstance.validateFields()) as TSuggestedFaqFormValues;
      await handleApproveSuggestedFaq({ ...values, ...target, originalQuestion: suggestion.question });
      closeApproveModal();
    } catch {}
  };

  const handleReject = async () => {
    await handleRejectSuggestedFaq();
    closeRejectModal();
  };

  return (
    <>
      <StyledItemBox>
        <StyledItemHeader>
          <StyledItemNumber>{index}</StyledItemNumber>
          <StyledItemTitle>{t('menu_suggested_faq')}</StyledItemTitle>
          {!isActionable && (
            <StatusPill status={suggestion.status}>{getSuggestedFaqStatusLabel(suggestion.status, t)}</StatusPill>
          )}
        </StyledItemHeader>
        <SuggestedFaqForm formInstance={formInstance} initialValues={initialValues} disabled={!isActionable} />
        {isActionable && (
          <StyledItemActions>
            <StyledApproveButton type="primary" onClick={handleOpenApproveModal}>
              {t('btn_approve')}
            </StyledApproveButton>
            <Button type="default" danger onClick={openRejectModal}>
              {t('btn_deny')}
            </Button>
          </StyledItemActions>
        )}
      </StyledItemBox>
      <ApproveFaqModal
        isOpen={isApproveModalOpen}
        onClose={closeApproveModal}
        onApprove={handleApprove}
        isLoading={isLoading}
        sourceChannels={suggestion.sourceChannels}
      />
      <RejectFaqModal
        isOpen={isRejectModalOpen}
        onClose={closeRejectModal}
        onReject={handleReject}
        isLoading={isLoading}
      />
    </>
  );
};

export default ConversationSuggestedFaqItem;
